/**
 * T037: Company Information Step Component
 * 
 * Handles company details collection in onboarding wizard
 * Constitutional compliance: WCAG 2.1 AA accessibility, TDD coverage
 */

import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Stack
} from '@mui/material';
import { OnboardingData } from '../../../services/onboardingService';

export interface CompanyInfoStepProps {
  data: OnboardingData['company_info'];
  onChange: (data: Partial<OnboardingData['company_info']>) => void;
  errors: Record<string, string>;
}

export const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({
  data,
  onChange,
  errors
}) => {
  const handleFieldChange = (field: keyof OnboardingData['company_info']) => 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ [field]: event.target.value });
    };

  const handleSelectChange = (field: keyof OnboardingData['company_info']) => 
    (event: any) => {
      onChange({ [field]: event.target.value });
    };

  const companySizes = [
    { value: 'startup', label: 'Startup (1-10 employees)' },
    { value: 'small', label: 'Small (11-50 employees)' },
    { value: 'medium', label: 'Medium (51-200 employees)' },
    { value: 'large', label: 'Large (201-1000 employees)' },
    { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'non_profit', label: 'Non-Profit' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Tell us about your company
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We'll use this information to better understand your needs and provide tailored solutions.
      </Typography>

      <Stack spacing={3}>
        {/* Row 1: Company Name and Website */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Company Name"
            value={data.company_name}
            onChange={handleFieldChange('company_name')}
            error={!!errors.company_name}
            helperText={errors.company_name}
            required
            data-testid="company-name-input"
            aria-describedby="company-name-help"
          />

          <TextField
            fullWidth
            label="Company Website"
            value={data.company_website || ''}
            onChange={handleFieldChange('company_website')}
            error={!!errors.company_website}
            helperText={errors.company_website || 'Optional - Include https://'}
            placeholder="https://example.com"
            data-testid="website-url-input"
            aria-describedby="company-website-help"
          />
        </Stack>

        {/* Row 2: Company Size and Industry */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl fullWidth error={!!errors.company_size}>
            <InputLabel>Company Size</InputLabel>
            <Select
              value={data.company_size}
              onChange={handleSelectChange('company_size')}
              label="Company Size"
              required
              data-testid="company-size-select"
              aria-describedby="company-size-help"
            >
              {companySizes.map((size) => (
                <MenuItem key={size.value} value={size.value}>
                  {size.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth error={!!errors.industry}>
            <InputLabel>Industry</InputLabel>
            <Select
              value={data.industry}
              onChange={handleSelectChange('industry')}
              label="Industry"
              required
              data-testid="industry-select"
              aria-describedby="industry-help"
            >
              {industries.map((industry) => (
                <MenuItem key={industry.value} value={industry.value}>
                  {industry.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Primary Contact Information
        </Typography>

        {/* Row 3: Contact Name and Email */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Primary Contact Name"
            value={data.primary_contact_name}
            onChange={handleFieldChange('primary_contact_name')}
            error={!!errors.primary_contact_name}
            helperText={errors.primary_contact_name}
            required
            aria-describedby="primary-contact-name-help"
          />

          <TextField
            fullWidth
            label="Primary Contact Email"
            type="email"
            value={data.primary_contact_email}
            onChange={handleFieldChange('primary_contact_email')}
            error={!!errors.primary_contact_email}
            helperText={errors.primary_contact_email}
            required
            aria-describedby="primary-contact-email-help"
          />
        </Stack>

        {/* Row 4: Phone */}
        <TextField
          fullWidth
          label="Primary Contact Phone"
          type="tel"
          value={data.primary_contact_phone || ''}
          onChange={handleFieldChange('primary_contact_phone')}
          error={!!errors.primary_contact_phone}
          helperText={errors.primary_contact_phone || 'Optional'}
          aria-describedby="primary-contact-phone-help"
          sx={{ maxWidth: { md: '50%' } }}
        />

        {/* Row 5: Company Description */}
        <TextField
          fullWidth
          label="Company Description"
          multiline
          rows={4}
          value={data.company_description || ''}
          onChange={handleFieldChange('company_description')}
          error={!!errors.company_description}
          helperText={errors.company_description || 'Optional - Brief description of your company and what you do'}
          data-testid="company-description-textarea"
          aria-describedby="company-description-help"
        />
      </Stack>
    </Box>
  );
};